import { conditionApplies, dateMatchesEventSchedule, resolvedEventBookingType, TDR } from 'tdr-common';
import { SearchFilters } from '../routes/AvailabilityGuard';

function eventAppliesToTable(event: TDR.Event, tableSlug: string): boolean {
	if (event.eventType === 'table') {
		return event.linkedTableSlug === tableSlug;
	}

	return true;
}

export function getEventDisplayTitle(event: TDR.Event): string {
	const tableDisplay = event.displays?.find((display) => display.location === 'table');
	return tableDisplay?.title ?? event.displays?.[0]?.title ?? event.name;
}

/**
 * True when the current search filters fall within a param event's constraints.
 * Multiple param events can be selected at once when their windows overlap
 * (e.g. 19:30 matching both 19:00–23:00 and 18:00–20:00).
 * Table-type events are never "selected" this way.
 */
export function isParamEventSelectedByFilters(
	event: TDR.Event,
	searchFilters: SearchFilters | undefined,
	timezone?: string
): boolean {
	if (!searchFilters || resolvedEventBookingType(event) !== 'param') {
		return false;
	}

	const { date, time, groupSize } = searchFilters;
	if (!date || !time || !groupSize) {
		return false;
	}

	if (!dateMatchesEventSchedule(date, event)) {
		return false;
	}

	const scenario: TDR.Ecommerce.Scenario = {
		date,
		time,
		groupSize
	};

	return conditionApplies(event.condition?.value, scenario, timezone);
}

export function getMatchingParamEvents(
	events: TDR.Event[] | undefined,
	searchFilters: SearchFilters | undefined,
	timezone?: string
): TDR.Event[] {
	if (!events?.length) {
		return [];
	}

	return events.filter((event) => isParamEventSelectedByFilters(event, searchFilters, timezone));
}

export function getMatchingEventsForTable(
	events: TDR.Event[] | undefined,
	searchFilters: SearchFilters,
	tableSlug: string,
	timezone?: string
): TDR.Event[] {
	if (!events?.length) {
		return [];
	}

	const { date, time, groupSize } = searchFilters;
	if (!date || !time || !groupSize) {
		return [];
	}

	const scenario: TDR.Ecommerce.Scenario = {
		date,
		time,
		groupSize
	};

	return events.filter((event) => {
		if (!eventAppliesToTable(event, tableSlug)) {
			return false;
		}

		if (!dateMatchesEventSchedule(date, event)) {
			return false;
		}

		return conditionApplies(event.condition?.value, scenario, timezone);
	});
}

export function excludeEventsById(
	events: TDR.Event[] | undefined,
	exclude: TDR.Event[]
): TDR.Event[] {
	if (!events?.length) {
		return [];
	}

	if (!exclude.length) {
		return events;
	}

	const excludeIds = new Set(exclude.map((event) => event.id));
	return events.filter((event) => !excludeIds.has(event.id));
}
